import axios from 'axios';

// 身份验证相关的基础URL
const AUTH_BASE_URL = '/api/auth';

// 在本地存储中保存令牌的键
export const TOKEN_STORAGE_KEY = 'auth_token';
export const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';
export const TOKEN_EXPIRY_KEY = 'token_expiry';
export const LAST_ACTIVITY_KEY = 'last_activity';

// 令牌过期前的刷新窗口 (10分钟，单位毫秒)
const REFRESH_TOKEN_THRESHOLD = 10 * 60 * 1000;

// 重置API URL（用于排除故障）
export const resetApiUrl = () => {
  localStorage.removeItem('api_url');
  console.log('API URL已重置，页面刷新后将提示重新输入');
  return true;
};

// 获取存储的令牌
export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

// 获取令牌过期时间
export const getTokenExpiry = (): number => {
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
  return expiryStr ? parseInt(expiryStr, 10) : 0;
};

// 设置令牌到本地存储
export const setTokens = (token: string, refreshToken: string, decodedToken?: any): void => {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  
  // 更新最后活动时间
  updateLastActivity();
  
  // 如果提供了解码后的令牌，存储过期时间
  if (decodedToken && decodedToken.exp) {
    localStorage.setItem(TOKEN_EXPIRY_KEY, (decodedToken.exp * 1000).toString());
  } else {
    // 否则计算大致过期时间 (当前时间 + 1小时)
    const expiryTime = Date.now() + 3600 * 1000;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  }
};

// 更新最后活动时间
export const updateLastActivity = (): void => {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
};

// 清除存储的令牌
export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
};

// 解析JWT令牌
export const parseJwt = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('解析JWT令牌失败:', e);
    return null;
  }
};

// 配置请求拦截器，为请求添加身份验证令牌
axios.interceptors.request.use(
  config => {
    // 记录用户活动
    updateLastActivity();
    
    const token = getStoredToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // 添加调试日志
    console.log(`API请求: ${config.method?.toUpperCase()} ${config.url}`, config);
    
    return config;
  },
  error => {
    console.error('API请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 登录API
export const login = async (username: string, password: string): Promise<any> => {
  try {
    console.log('尝试登录:', { username, apiUrl: axios.defaults.baseURL });
    
    // 设置超时时间，避免长时间等待
    const response = await axios.post(`${AUTH_BASE_URL}/login`, { username, password }, {
      timeout: 8000 // 8秒超时
    });
    console.log('登录成功响应:', response.data);
    
    // 严格验证API响应
    if (!response.data || 
        typeof response.data !== 'object' || 
        !response.data.token || 
        !response.data.refreshToken ||
        !response.data.user ||
        !response.data.user.id) {
      console.error('API响应格式无效:', response.data);
      return {
        success: false,
        message: 'API响应格式无效，请联系系统管理员',
        validationFailed: true
      };
    }
    
    // 解析JWT令牌以获取过期时间
    const decodedToken = parseJwt(response.data.token);
    
    // 存储令牌
    setTokens(response.data.token, response.data.refreshToken, decodedToken);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('登录错误:', error);
    
    // 处理网络错误
    if (error.code === 'ECONNABORTED') {
      return {
        success: false,
        message: '登录请求超时，请检查您的网络连接并重试'
      };
    }
    
    // 处理API响应错误
    if (error.response) {
      const statusCode = error.response.status;
      if (statusCode === 401) {
        return {
          success: false,
          message: '用户名或密码不正确'
        };
      }
      
      return {
        success: false,
        message: error.response.data?.message || '登录失败，请重试'
      };
    }
    
    // 处理其他错误
    return {
      success: false,
      message: '登录失败，请检查您的网络连接并重试',
      error
    };
  }
};

// 登出API
export const logout = async (): Promise<any> => {
  try {
    // 清除令牌
    clearTokens();
    
    return {
      success: true,
      message: '登出成功'
    };
  } catch (error) {
    console.error('登出错误:', error);
    return {
      success: false,
      message: '登出失败',
      error
    };
  }
};

// 刷新令牌API
export const refreshToken = async (): Promise<any> => {
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    
    if (!refreshToken) {
      return {
        success: false,
        message: '没有可用的刷新令牌',
        needsReauth: true
      };
    }

    // 检查刷新令牌是否可能已过期
    try {
      const decodedRefreshToken = parseJwt(refreshToken);
      if (decodedRefreshToken && decodedRefreshToken.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (currentTime >= decodedRefreshToken.exp) {
          // 刷新令牌已过期
          return {
            success: false,
            message: '会话已过期（超过7天未使用），请重新登录',
            needsReauth: true,
            expired: true
          };
        }
      }
    } catch (parseError) {
      console.error('解析刷新令牌失败:', parseError);
    }
    
    const response = await axios.post(`${AUTH_BASE_URL}/refresh-token`, { refreshToken }, {
      timeout: 5000 // 5秒超时
    });
    
    // 解析JWT令牌以获取过期时间
    const decodedToken = parseJwt(response.data.token);
    
    // 存储新令牌
    if (response.data.token && response.data.refreshToken) {
      setTokens(response.data.token, response.data.refreshToken, decodedToken);
    }
    
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    console.error('刷新令牌错误:', error);
    
    // 处理网络错误
    if (error.code === 'ECONNABORTED') {
      return {
        success: false,
        message: '网络连接超时，请检查网络连接后重试',
        networkError: true
      };
    }
    
    // 处理服务器响应错误
    if (error.response) {
      const statusCode = error.response.status;
      if (statusCode === 401) {
        return {
          success: false,
          message: '会话已过期（超过7天未使用），请重新登录',
          needsReauth: true,
          expired: true
        };
      } else if (statusCode === 403) {
        return {
          success: false,
          message: '刷新令牌无效，请重新登录',
          needsReauth: true
        };
      } else if (statusCode >= 500) {
        return {
          success: false,
          message: '服务器暂时无法连接，请稍后重试',
          serverError: true
        };
      }
    }
    
    return {
      success: false,
      message: '令牌刷新失败，可能是网络问题或会话过期，请重新登录',
      needsReauth: true,
      error
    };
  }
};

// 检查令牌是否需要刷新
export const shouldRefreshToken = (): boolean => {
  const tokenExpiry = getTokenExpiry();
  
  // 如果没有过期时间，不需要刷新
  if (!tokenExpiry) return false;
  
  // 如果令牌接近过期（还有10分钟过期），则刷新
  return Date.now() > tokenExpiry - REFRESH_TOKEN_THRESHOLD;
};

// 设置自动刷新令牌的定时器
export const setupTokenRefresh = (): void => {
  // 每分钟检查一次令牌是否需要刷新
  const interval = setInterval(async () => {
    try {
      const token = getStoredToken();
      const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      
      // 如果没有令牌或刷新令牌，清除定时器
      if (!token || !refreshTokenValue) {
        clearInterval(interval);
        return;
      }
      
      // 检查是否需要刷新令牌
      if (shouldRefreshToken()) {
        console.log('令牌即将过期，正在刷新...');
        const refreshResult = await refreshToken();
        
        // 如果刷新失败且是因为过期，显示提示并清除定时器
        if (!refreshResult.success && refreshResult.needsReauth) {
          console.log('自动刷新失败:', refreshResult.message);
          clearInterval(interval);
          
          // 使用全局会话过期处理函数（如果可用）
          const globalHandler = (window as any).__sessionExpiredHandler;
          if (globalHandler) {
            globalHandler(refreshResult);
          } else {
            // 回退到原有的处理方式
            if (refreshResult.expired) {
              alert('会话已过期（超过7天未使用），请重新登录以继续使用系统。');
            } else {
              alert('会话状态异常，请重新登录以继续使用系统。');
            }
            // 跳转到登录页面
            window.location.href = '/login';
          }
          return;
        }
      }
      
      // 检查用户活动情况
      const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (lastActivityStr) {
        const lastActivity = parseInt(lastActivityStr, 10);
        const inactiveTime = Date.now() - lastActivity;
        
        // 如果用户超过6小时不活动，则自动登出
        // 这个阈值可以根据业务需求调整
        if (inactiveTime > 6 * 60 * 60 * 1000) {
          console.log('用户长时间不活动，自动登出');
          clearTokens();
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('令牌自动刷新错误:', error);
    }
  }, 60000); // 每分钟检查一次
  
  // 监听用户交互，更新最后活动时间
  const updateActivity = () => updateLastActivity();
  window.addEventListener('click', updateActivity);
  window.addEventListener('keypress', updateActivity);
  window.addEventListener('scroll', updateActivity);
  window.addEventListener('mousemove', updateActivity);
};

// 配置响应拦截器处理令牌过期情况
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

axios.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;
    
    // 如果响应状态是401（未授权）且尚未尝试刷新令牌
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // 如果已经在刷新令牌，则将请求加入队列
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const refreshResult = await refreshToken();
        
        if (refreshResult.success) {
          const newToken = getStoredToken();
          
          // 更新原始请求的头部并重试
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          // 处理队列中的其他请求
          processQueue(null, newToken);
          
          return axios(originalRequest);
        } else {
          // 刷新令牌失败，清除所有令牌并处理队列
          clearTokens();
          
          // 根据不同的失败原因给出不同的提示
          let errorMessage = 'Session expired';
          let userMessage = '会话已过期，请重新登录';
          
          if (refreshResult.expired) {
            userMessage = '会话已过期（超过7天未使用），请重新登录以继续使用系统';
            errorMessage = 'Session expired after 7 days of inactivity';
          } else if (refreshResult.networkError) {
            userMessage = '网络连接异常，请检查网络后重新登录';
            errorMessage = 'Network error during token refresh';
          } else if (refreshResult.serverError) {
            userMessage = '服务器连接失败，请稍后重试或重新登录';
            errorMessage = 'Server error during token refresh';
          }
          
          processQueue(new Error(errorMessage));
          
          // 仅在用户可见界面显示重新登录提示
          // 只有当请求不是静默后台请求时才跳转
          if (!originalRequest.headers['X-Silent-Request']) {
            console.log('令牌刷新失败:', userMessage);
            
            // 使用全局会话过期处理函数（如果可用）
            const globalHandler = (window as any).__sessionExpiredHandler;
            if (globalHandler) {
              globalHandler(refreshResult);
            } else {
              // 回退到原有的处理方式
              if (window.confirm) {
                const shouldRedirect = window.confirm(
                  `${userMessage}\n\n点击"确定"跳转到登录页面，点击"取消"稍后重试。`
                );
                if (shouldRedirect) {
                  window.location.href = '/login';
                }
              } else {
                // 如果没有confirm方法，直接跳转
                alert(userMessage);
                window.location.href = '/login';
              }
            }
          }
          
          return Promise.reject(new Error(errorMessage));
        }
      } catch (refreshError) {
        // 处理队列中的其他请求
        processQueue(refreshError);
        
        // 清除令牌
        clearTokens();
        
        // 仅在用户可见界面显示重新登录提示
        if (!originalRequest.headers['X-Silent-Request']) {
          console.log('令牌刷新异常:', refreshError);
          
          const userMessage = '会话状态异常，请重新登录以继续使用系统';
          
          // 使用全局会话过期处理函数（如果可用）
          const globalHandler = (window as any).__sessionExpiredHandler;
          if (globalHandler) {
            // 创建一个错误信息对象
            const errorInfo = {
              message: userMessage,
              isExpired: false,
              isNetworkError: false,
              needsReauth: true
            };
            globalHandler(errorInfo);
          } else {
            // 回退到原有的处理方式
            if (window.confirm) {
              const shouldRedirect = window.confirm(
                `${userMessage}\n\n点击"确定"跳转到登录页面，点击"取消"稍后重试。`
              );
              if (shouldRedirect) {
                window.location.href = '/login';
              }
            } else {
              // 如果没有confirm方法，直接跳转
              alert(userMessage);
              window.location.href = '/login';
            }
          }
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// 更新用户凭据API
export const updateCredentials = async (
  currentPassword: string, 
  newPassword: string, 
  newUsername?: string
): Promise<any> => {
  try {
    const response = await axios.put(`${AUTH_BASE_URL}/update-credentials`, {
      currentPassword,
      newPassword,
      newUsername
    });
    
    // 如果提供了新的用户名，可能需要更新一些本地状态
    
    return {
      success: true,
      message: response.data.message || '用户凭据更新成功',
      data: response.data
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || '更新用户凭据失败',
      error
    };
  }
}; 